import { alpha, createTheme } from '@mui/material/styles';

const jobsDockOffset = 56;

const md3 = {
  primary: '#006B5E',
  onPrimary: '#FFFFFF',
  primaryContainer: '#D4F5EC',
  onPrimaryContainer: '#00201B',
  secondary: '#3F5F86',
  onSecondary: '#FFFFFF',
  secondaryContainer: '#D6E6FF',
  onSecondaryContainer: '#081D32',
  tertiary: '#8B5E00',
  onTertiary: '#FFFFFF',
  tertiaryContainer: '#FFE2A8',
  onTertiaryContainer: '#2C1B00',
  error: '#BA1A1A',
  onError: '#FFFFFF',
  errorContainer: '#FFDAD6',
  onErrorContainer: '#410002',
  background: '#F7F9F8',
  onBackground: '#191C1B',
  surface: '#FBFDFB',
  surfaceDim: '#D9DDDA',
  surfaceContainerLowest: '#FFFFFF',
  surfaceContainerLow: '#F2F5F2',
  surfaceContainer: '#ECEFEC',
  surfaceContainerHigh: '#E6E9E6',
  surfaceContainerHighest: '#E0E4E0',
  onSurface: '#191C1B',
  onSurfaceVariant: '#404944',
  outline: '#707974',
  outlineVariant: '#C0C9C4',
};

const focusRing = `0 0 0 3px ${alpha(md3.primary, 0.28)}`;

const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'light',
    primary: {
      main: md3.primary,
      contrastText: md3.onPrimary,
    },
    secondary: {
      main: md3.secondary,
      contrastText: md3.onSecondary,
    },
    warning: {
      main: md3.tertiary,
      contrastText: md3.onTertiary,
    },
    error: {
      main: md3.error,
      contrastText: md3.onError,
    },
    success: {
      main: '#176B37',
      contrastText: '#FFFFFF',
    },
    background: {
      default: md3.background,
      paper: md3.surface,
    },
    text: {
      primary: md3.onSurface,
      secondary: md3.onSurfaceVariant,
    },
    divider: alpha(md3.outlineVariant, 0.86),
    action: {
      hover: alpha(md3.primary, 0.08),
      selected: alpha(md3.primary, 0.14),
      focus: alpha(md3.primary, 0.18),
      disabledBackground: alpha(md3.onSurface, 0.08),
      disabled: alpha(md3.onSurface, 0.38),
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'sans-serif',
    ].join(','),
    allVariants: {
      letterSpacing: 0,
    },
    h1: {
      fontSize: '2.25rem',
      lineHeight: 1.15,
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.875rem',
      lineHeight: 1.2,
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.5rem',
      lineHeight: 1.25,
      fontWeight: 700,
    },
    h4: {
      fontSize: '1.25rem',
      lineHeight: 1.3,
      fontWeight: 700,
    },
    h5: {
      fontSize: '1.125rem',
      lineHeight: 1.35,
      fontWeight: 700,
    },
    h6: {
      fontSize: '1rem',
      lineHeight: 1.4,
      fontWeight: 700,
    },
    subtitle1: {
      fontWeight: 650,
      lineHeight: 1.45,
    },
    subtitle2: {
      fontWeight: 650,
      lineHeight: 1.4,
    },
    button: {
      fontWeight: 650,
      letterSpacing: 0,
      textTransform: 'none',
    },
    overline: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.4,
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          colorScheme: 'light',
          '--md-sys-color-primary': md3.primary,
          '--md-sys-color-on-primary': md3.onPrimary,
          '--md-sys-color-primary-container': md3.primaryContainer,
          '--md-sys-color-on-primary-container': md3.onPrimaryContainer,
          '--md-sys-color-secondary': md3.secondary,
          '--md-sys-color-secondary-container': md3.secondaryContainer,
          '--md-sys-color-tertiary': md3.tertiary,
          '--md-sys-color-surface-container-low': md3.surfaceContainerLow,
          '--md-sys-color-surface-container': md3.surfaceContainer,
          '--md-sys-color-surface-container-high': md3.surfaceContainerHigh,
          '--md-sys-color-outline': md3.outline,
          '--md-sys-color-outline-variant': md3.outlineVariant,
        },
        'html, body, #root': {
          minHeight: '100%',
        },
        body: {
          backgroundColor: md3.background,
          color: md3.onSurface,
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        '*:focus': {
          outline: 'none',
        },
        '*:focus-visible': {
          outline: `3px solid ${md3.primary}`,
          outlineOffset: 2,
        },
        '::selection': {
          backgroundColor: alpha(md3.primary, 0.22),
        },
        a: {
          color: md3.primary,
          textUnderlineOffset: '0.18em',
        },
        '.skip-link': {
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: 2000,
          transform: 'translateY(-140%)',
          transition: 'transform 120ms ease',
          borderRadius: 8,
          backgroundColor: md3.primary,
          color: md3.onPrimary,
          padding: '10px 14px',
          fontWeight: 700,
          textDecoration: 'none',
        },
        '.skip-link:focus-visible': {
          transform: 'translateY(0)',
          outline: `3px solid ${md3.onPrimary}`,
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            scrollBehavior: 'auto !important',
            transitionDuration: '0.01ms !important',
          },
        },
        '@media (forced-colors: active)': {
          '*:focus-visible': {
            outline: '3px solid Highlight',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderColor: alpha(md3.outlineVariant, 0.88),
        },
        rounded: {
          borderRadius: 8,
        },
        outlined: {
          borderColor: alpha(md3.outlineVariant, 0.9),
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: md3.surface,
          backgroundImage: 'none',
          borderRight: `1px solid ${alpha(md3.outlineVariant, 0.9)}`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: 8,
          fontWeight: 650,
          textTransform: 'none',
          '&.Mui-focusVisible': {
            boxShadow: focusRing,
          },
        },
        sizeSmall: {
          minHeight: 40,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: alpha(md3.outline, 0.68),
          color: md3.primary,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          borderRadius: 8,
          '&.Mui-focusVisible': {
            boxShadow: focusRing,
          },
        },
        sizeSmall: {
          minWidth: 40,
          minHeight: 40,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: 8,
          marginBlock: 2,
          color: md3.onSurfaceVariant,
          '& .MuiListItemIcon-root': {
            color: 'inherit',
          },
          '&.Mui-selected': {
            backgroundColor: md3.primaryContainer,
            color: md3.onPrimaryContainer,
            fontWeight: 700,
            '&:hover': {
              backgroundColor: md3.primaryContainer,
            },
          },
          '&.Mui-focusVisible': {
            boxShadow: focusRing,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: md3.onSurfaceVariant,
          minWidth: 36,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: md3.surfaceContainerLowest,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(md3.outline, 0.5),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: md3.outline,
          },
          '&.Mui-focused': {
            backgroundColor: md3.surface,
            boxShadow: focusRing,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: md3.primary,
            borderWidth: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: md3.onSurfaceVariant,
          '&.Mui-focused': {
            color: md3.primary,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${alpha(md3.outlineVariant, 0.9)}`,
          backgroundColor: md3.surfaceContainerLowest,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: 6,
          marginInline: 6,
          marginBlock: 2,
          '&.Mui-focusVisible': {
            boxShadow: focusRing,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: md3.surfaceContainerLowest,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: alpha(md3.outlineVariant, 0.72),
        },
        head: {
          backgroundColor: md3.surfaceContainerHigh,
          color: md3.onSurface,
          fontWeight: 700,
        },
        sizeSmall: {
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(md3.primary, 0.06),
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(md3.primary, 0.12),
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${alpha(md3.outlineVariant, 0.72)}`,
          backgroundColor: md3.surfaceContainerLow,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 650,
        },
        colorDefault: {
          backgroundColor: md3.surfaceContainerHigh,
          color: md3.onSurfaceVariant,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          padding: 2,
          borderRadius: 8,
          backgroundColor: md3.surfaceContainer,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 40,
          border: 0,
          borderRadius: 6,
          color: md3.onSurfaceVariant,
          '&.Mui-selected': {
            backgroundColor: md3.surfaceContainerLowest,
            color: md3.primary,
            boxShadow: `inset 0 0 0 1px ${alpha(md3.outlineVariant, 0.8)}`,
          },
          '&.Mui-focusVisible': {
            boxShadow: focusRing,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          fontWeight: 650,
          textTransform: 'none',
          '&.Mui-focusVisible': {
            boxShadow: focusRing,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${alpha(md3.outlineVariant, 0.78)}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          marginBottom: `${32 + jobsDockOffset}px`,
          maxHeight: `calc(100% - ${64 + jobsDockOffset}px)`,
          border: `1px solid ${alpha(md3.outlineVariant, 0.9)}`,
          borderRadius: 8,
          backgroundColor: md3.surfaceContainerLowest,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 8,
          backgroundColor: md3.surfaceContainerHigh,
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
  },
});

export default theme;
